/**
 * What a collection command starts with when it is opened from a related list
 * (04_frontend_spec.md §6.6): a lead's Notes list is declared with
 * `params: {subject_type: "lead", subject_id: "{lead_id}"}`, so Add note…
 * opened there must already know its subject.
 *
 * The rule is the same for every object and has no field names in it: each
 * resolved related-list param that names a property of the command's request
 * schema fills that property, and is locked, because the person chose the
 * parent record, not the value. Params the command has no property for (a
 * filter that only narrows the list) are left out, and fields the params do
 * not name stay as the command's own defaults and dry run make them.
 *
 * Pure: the pages hand the result to the command shell, which sends it with
 * its first dry run.
 */
import type { CommandBody } from "../../data/object-ui-adapter";
import {
  BULK_TARGETS_FIELD,
  PLATFORM_BODY_FIELDS,
  type JsonSchema,
  type ManifestCommand,
} from "./contract";
import type { ListQuery } from "./list-query";
import { effectiveSchema } from "./values";

/** A related list's resolved params (or a list's filters): name → value(s). */
export type PrefillParams = Readonly<
  Record<string, string | readonly string[]>
>;

export interface CommandPrefill {
  /** The body the command opens with (and its first dry run is sent). */
  body: CommandBody;
  /** The body paths that came from the parent record and cannot be edited. */
  lockedPaths: string[];
}

function typeOf(schema: JsonSchema): string | undefined {
  const type = effectiveSchema(schema).type;
  return Array.isArray(type) ? type.find((t) => t !== "null") : type;
}

/** One param value read as the type the schema declares; undefined when it can't be. */
function coerce(schema: JsonSchema, raw: string): unknown {
  switch (typeOf(schema)) {
    case "integer": {
      const n = Number(raw);
      return raw.trim() !== "" && Number.isInteger(n) ? n : undefined;
    }
    case "number": {
      const n = Number(raw);
      return raw.trim() !== "" && Number.isFinite(n) ? n : undefined;
    }
    case "boolean":
      return raw === "true" ? true : raw === "false" ? false : undefined;
    default:
      return raw;
  }
}

function valueFor(
  schema: JsonSchema,
  value: string | readonly string[],
): unknown {
  const values = typeof value === "string" ? [value] : value;
  if (typeOf(schema) === "array") {
    const items = effectiveSchema(schema).items ?? {};
    const coerced = values.map((v) => coerce(items, v));
    return coerced.some((v) => v === undefined) ? undefined : coerced;
  }
  return values.length === 1 ? coerce(schema, values[0]!) : undefined;
}

/** What `command` starts with when opened under a list whose params are `params`. */
export function prefillOf(
  command: ManifestCommand,
  params: PrefillParams,
): CommandPrefill {
  const properties = command.request_schema?.properties ?? {};
  const body: CommandBody = {};
  const lockedPaths: string[] = [];
  const skipped: string[] = [];
  for (const [name, value] of Object.entries(params)) {
    if (
      !Object.prototype.hasOwnProperty.call(properties, name) ||
      name === BULK_TARGETS_FIELD ||
      (PLATFORM_BODY_FIELDS as readonly string[]).includes(name)
    )
      continue;
    const filled = valueFor(properties[name]!, value);
    if (filled === undefined) {
      skipped.push(name);
      continue;
    }
    body[name] = filled;
    lockedPaths.push(name);
  }
  if (skipped.length > 0) {
    console.warn("object_ui.related.prefill_skipped", {
      command: command.id,
      params: skipped,
    });
  }
  return { body, lockedPaths };
}

/**
 * What a collection command starts with when it is opened from a record's own page and names that record's object
 * in an entity field (Request merchandise quote… on a customer: its `customer_code` picks `customers.customer`):
 * that field starts filled with the record and locked, because the person chose the record by opening it. Same rule
 * as a related list's, with no field names in it: every entity field over `objectId` that holds one identifier.
 */
export function recordPrefillOf(
  command: ManifestCommand,
  objectId: string,
  recordKey: string,
): CommandPrefill {
  const properties = command.request_schema?.properties ?? {};
  const body: CommandBody = {};
  const lockedPaths: string[] = [];
  for (const [name, property] of Object.entries(properties)) {
    const schema = effectiveSchema(property);
    if (
      schema["x-cis-field"]?.entity === objectId &&
      schema["x-cis-field"]?.widget === "entity" &&
      typeOf(schema) === "string"
    ) {
      body[name] = recordKey;
      lockedPaths.push(name);
    }
  }
  return { body, lockedPaths };
}

/** The filters of a list reached through a related list, still as that record set them. */
export function prefillParamsOf(query: ListQuery): PrefillParams {
  return Object.fromEntries(
    query.prefill.flatMap((name) => {
      const value = query.filters[name];
      return value === undefined ? [] : [[name, value] as const];
    }),
  );
}
